import { LearnerTrainingStatus, PretestOutcome, PretestSkipRule } from '~/data';
import { Chapter, Sequence } from '../navigation/NavigationSlice';
import { Pretest } from './PretestSlice';

/**
 * What a graded pretest gave the learner, from the server's answer only:
 * - completed: the training itself is completed (the run ended), so the normal completion follows;
 * - passed: under the passing-score rule, the assessment met its passing score, and chapters it did not
 *   cover remain;
 * - chapters: chapters answered entirely right were tested out (below the passing score, or under the
 *   strict rule, which never passes or fails the assessment as a whole);
 * - none: nothing was tested out.
 * fullCourseEligible and the learner's own answers never decide completion.
 */
export type PretestOutcomeKind = 'completed' | 'passed' | 'chapters' | 'none';

export const pretestOutcomeKind = (
  pretest: Pretest,
  status: LearnerTrainingStatus
): PretestOutcomeKind => {
  if (status === LearnerTrainingStatus.Completed) return 'completed';
  if (
    pretest.skipRule === PretestSkipRule.PassingScore &&
    pretest.passed === true
  )
    return 'passed';
  return pretest.results.some(r => r.outcome === PretestOutcome.TestedOut)
    ? 'chapters'
    : 'none';
};

/**
 * Each chapter after grading: tested out, or still to take. A chapter the pretest did not cover has no
 * result, so the learner takes it like any other.
 */
export type PretestChapterRow = {
  id: string;
  number: number;
  title: string;
  testedOut: boolean;
};

export const pretestChapterRows = (
  items: Array<Chapter | Sequence>,
  pretest: Pretest
): PretestChapterRow[] =>
  items
    .filter((item): item is Chapter => item.kind === 'chapter')
    .map((chapter, index) => ({
      id: chapter.id,
      number: index + 1,
      title: chapter.title,
      testedOut: pretest.results.some(
        r =>
          r.chapterId === chapter.id && r.outcome === PretestOutcome.TestedOut
      )
    }));
